import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { aaiApi } from '../../services/aaiApi';
import type { AaiRequest } from '../../types/aai';
import {
  Action, Chip, Divided, EmptyState, ErrorNote, ListRow, Loading, PageBody, PageHead, Panel,
  PanelHead, Stat, StatusChip,
} from '../../components/aa';
import type { RowMeta } from '../../components/aa';
import { useRecordLinks } from '../../hooks/useRecordLinks';

/*
 * The expert's queue: the work that carries their name.
 *
 * Being named on a request is how work is given to somebody here, and until
 * this screen existed there was nowhere to read that. An expert opened the same
 * Requests page everyone else opens, which asks "what would help?" and lists
 * the company's requests underneath, so the two things they were given sat in a
 * list of forty as two more grey rows. Nothing answered "what am I on".
 *
 * So the queue is grouped by what the work needs NEXT, not by status name. A
 * status is a fact about a record; what it needs next is a fact about the
 * person reading it, and that is the only reason to open a queue at all.
 *
 * Work named to them in ANOTHER company rides in `assigned_elsewhere` and is
 * shown with the client's name on it, because a client's project reading as
 * your own company's work is the one thing it must never do.
 */

type Bucket = 'build' | 'waiting' | 'done';

const BUCKETS: { key: Bucket; title: string; desc: string }[] = [
  {
    key: 'build',
    title: 'Yours to build',
    desc: 'Approved and named to you. Build it, then publish it against the request to close it.',
  },
  {
    key: 'waiting',
    title: 'Not yours yet',
    desc: 'Named to you, but still going through approval. Nothing to do until it clears.',
  },
  {
    key: 'done',
    title: 'Delivered',
    desc: 'The tool is published and the person who asked has it.',
  },
];

const bucketOf = (r: AaiRequest): Bucket => {
  if (r.tool_skill_id) return 'done';
  if (r.status === 'approved') return 'build';
  return 'waiting';
};

/* What this piece of work is waiting on, in the words of the person who has it. */
const nextFor = (r: AaiRequest): string => {
  if (r.tool_skill_id) return 'Requester runs it and marks the outcome';
  // Before approval it is waiting on somebody else: the same words as every
  // other screen, from the server's routing.
  if (r.status !== 'approved' && r.display) return r.display.next;
  if (r.status === 'pending') return 'Department head review';
  if (r.status === 'needs_info') return 'Requester adds the detail asked for';
  if (r.status === 'dept_approved') return 'Champion or owner sign-off';
  if (r.build_state === 'tested') return 'Publish it against the request';
  if (r.build_state === 'built') return 'Test it, then publish it';
  return 'Build it';
};

const routeWord = (r: AaiRequest): string => {
  if (r.route === 'quick_win') return 'Quick Win';
  if (r.route === 'project') return 'Delivery Project';
  return 'Not routed';
};

const STATE: Record<string, string> = {
  pending: 'pending',
  needs_info: 'needs_review',
  dept_approved: 'in_progress',
  approved: 'in_delivery',
  declined: 'declined',
};

const WorkRow: React.FC<{ request: AaiRequest; onOpen: () => void }> = ({ request, onOpen }) => {
  const meta: RowMeta[] = [
    { label: 'Asked by', value: request.requester_email, foot: request.department || 'No department' },
    { label: 'Next', value: nextFor(request) },
    { label: 'Asked for as', value: routeWord(request), foot: `${request.risk || 'low'} risk` },
  ];
  if (request.company_name) {
    meta.unshift({ label: 'Client', value: request.company_name, foot: 'You deliver this' });
  }
  let badge = <StatusChip status={STATE[request.status] || request.status} />;
  if (request.tool_skill_id) badge = <StatusChip status="ready" />;
  return (
    <ListRow
      mark={<span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ink text-[13px] font-bold text-white">W</span>}
      title={request.title}
      badge={(
        <>
          {badge}
          {request.build_state && <Chip tone="ok">{request.build_state === 'tested' ? 'Tested' : 'Built'}</Chip>}
          {request.company_name && <Chip tone="private">Client work</Chip>}
        </>
      )}
      desc={request.detail}
      meta={meta}
      arrow
      onClick={onOpen}
    />
  );
};

export const MyWorkPage: React.FC = () => {
  const [params] = useSearchParams();
  const openParam = params.get('open');
  const links = useRecordLinks();

  useEffect(() => {
    if (openParam) links.project(openParam, true);
  }, [openParam, links]);
  const [mine, setMine] = useState<AaiRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const me = (localStorage.getItem('user_email') || '').toLowerCase();

  const load = useCallback(async () => {
    try {
      const res = await aaiApi.listRequests();
      const here = res.data.requests || [];
      const elsewhere = res.data.assigned_elsewhere || [];
      // `requests` is the whole company for anyone who can see the company, so
      // the name is what narrows it. `assigned_elsewhere` is already narrowed:
      // it exists only because they were named on it.
      const named = here.filter((r) => (r.assigned_expert_email || '').toLowerCase() === me);
      setMine([...named, ...elsewhere]);
      setError(null);
    } catch {
      setError('Could not load your work.');
    } finally {
      setLoading(false);
    }
  }, [me]);
  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const out: Record<Bucket, AaiRequest[]> = { build: [], waiting: [], done: [] };
    mine.forEach((r) => {
      if (r.status === 'declined') return;
      out[bucketOf(r)].push(r);
    });
    return out;
  }, [mine]);


  if (loading) return <PageBody><Loading label="Opening your work" /></PageBody>;

  return (
    <PageBody>
      <PageHead
        align="center"
        eyebrow="Delivery"
        title="My work"
        sub="Every request someone put your name on. Build what is yours, then publish it against the request, which is what hands it to the person who asked."
      />

      {error && <ErrorNote message={error} />}

      {!error && mine.length === 0 && (
        <Panel>
          <EmptyState
            title="Nothing is assigned to you"
            reason="A champion or owner names who delivers a request when they approve it. Anything given to you appears here, including work in another company you deliver for."
          />
        </Panel>
      )}

      {!error && mine.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-3">
          <Stat label="Yours to build" value={grouped.build.length} />
          <Stat label="Not yours yet" value={grouped.waiting.length} />
          <Stat label="Delivered" value={grouped.done.length} />
        </div>
      )}

      {BUCKETS.map((bucket) => {
        const rows = grouped[bucket.key];
        if (rows.length === 0) return null;
        return (
          <Panel key={bucket.key} className="mb-6">
            <PanelHead kicker={`${rows.length} in this group`} title={bucket.title} desc={bucket.desc} />
            <Divided>
              {rows.map((r) => <WorkRow key={r.id} request={r} onOpen={() => links.project(r.id)} />)}
            </Divided>
          </Panel>
        );
      })}

      {!error && mine.length > 0 && grouped.build.length === 0 && (
        <p className="m-0 text-[12.5px] text-ink-soft">
          Nothing is waiting on you right now. <Action size="sm" variant="quiet" to="/aai/prd-reviews">Open the PRD queue</Action>
        </p>
      )}

    </PageBody>
  );
};

export default MyWorkPage;
